(function () {
  'use strict';

  // Sub-pixel layout means scrollWidth is routinely a pixel or three larger than
  // clientWidth even when nothing actually overflows — three cards at
  // calc((100% - gap*2) / 3) is exactly that case. A 1px tolerance treated that
  // rounding noise as real overflow, revealed the arrows, and then scrolled by
  // two pixels when pressed. Anything under this threshold is not a page worth
  // offering.
  var OVERFLOW_TOLERANCE = 16;

  // Progressive enhancement. The track is a CSS scroll-snap container and works
  // without this script; the arrows ship hidden and are only revealed here, so
  // no-JS never shows dead buttons.
  class SpikeyCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.controls = this.querySelector('[data-carousel-controls]');
      if (!this.track || !this.controls) return;

      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      if (!this.prev || !this.next) return;

      this.onPrev = this.scrollByPage.bind(this, -1);
      this.onNext = this.scrollByPage.bind(this, 1);
      this.update = this.update.bind(this);

      this.prev.addEventListener('click', this.onPrev);
      this.next.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.update, { passive: true });

      // Re-evaluate on layout change. Whether arrows are needed depends on the
      // per-view count, which changes at the 750px breakpoint — checking only
      // once at connect left the state stale after any resize or rotation.
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.update);
        this.observer.observe(this.track);
      } else {
        window.addEventListener('resize', this.update);
      }

      this.update();
    }

    disconnectedCallback() {
      if (this.prev) this.prev.removeEventListener('click', this.onPrev);
      if (this.next) this.next.removeEventListener('click', this.onNext);
      if (this.track) this.track.removeEventListener('scroll', this.update);
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('resize', this.update);
    }

    maxScroll() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    overflows() {
      return this.maxScroll() > OVERFLOW_TOLERANCE;
    }

    // Single source of truth for both visibility and the disabled states, so
    // they can never disagree.
    update() {
      if (!this.overflows()) {
        this.controls.hidden = true;
        return;
      }

      this.controls.hidden = false;

      var max = this.maxScroll();
      this.prev.disabled = this.track.scrollLeft <= OVERFLOW_TOLERANCE;
      this.next.disabled = this.track.scrollLeft >= max - OVERFLOW_TOLERANCE;
    }

    scrollByPage(direction) {
      var first = this.track.querySelector('li');
      if (!first) {
        this.track.scrollBy({ left: this.track.clientWidth * direction, behavior: this.scrollBehavior() });
        return;
      }

      // Read the real gap rather than assuming one. This was hardcoded at 16px,
      // which under-paged any track with a wider gap — the testimonials carousel
      // uses 24px on mobile and 32px on desktop.
      var styles = window.getComputedStyle(this.track);
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;
      var step = first.getBoundingClientRect().width + gap;

      this.track.scrollBy({ left: step * direction, behavior: this.scrollBehavior() });
    }

    scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }
  }

  if (!customElements.get('spikey-carousel')) {
    customElements.define('spikey-carousel', SpikeyCarousel);
  }
})();
